import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { MapPin, Clock, Phone } from "lucide-react";
import { useLanguage } from "@/LanguageContext";

const HomePage = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero Section */}
      <section
        className="relative min-h-[650px] flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background Spice Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1716816211590-c15a328a5ff0?w=1920')`,
          }}
        />
        {/* Dark Overlay for text readability */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(rgba(10, 5, 2, 0.55), rgba(25, 12, 8, 0.7))',
          }}
        />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10 relative">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-6xl sm:text-7xl lg:text-8xl font-script mb-4 gold-glow"
            style={{ color: '#d4af37' }}
            data-testid="hero-title"
          >
            Masala Indisk
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg sm:text-xl mb-8 font-lora italic"
            style={{ color: '#f5e6d3' }}
          >
            {t("hero_subtitle")}
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link to="/menu" data-testid="hero-menu-button">
              <Button
                size="lg"
                className="text-lg px-10 py-6 font-serif-display btn-maroon rounded-full"
              >
                {t("hero_menu_btn")}
              </Button>
            </Link>
            <Link to="/booking" data-testid="hero-booking-button">
              <Button
                size="lg"
                className="text-lg px-10 py-6 font-serif-display btn-gold rounded-full"
              >
                {t("hero_booking_btn")}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Decorative Divider */}
      <div className="h-2" style={{ background: 'linear-gradient(90deg, #721111, #d4af37, #721111)' }}></div>

      {/* About Us Section */}
      <section className="py-20" style={{ background: '#f4e7d8' }} data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="indian-divider mb-6">
            <span className="text-sm uppercase tracking-widest" style={{ color: '#8b4513' }}>{t("home_about_tag")}</span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl sm:text-4xl font-serif-display font-semibold mb-6 italic" style={{ color: '#2d1810' }}>
                {t("home_about_title")}
              </h2>
              <p className="text-base mb-4 leading-relaxed" style={{ color: '#4a3728' }}>
                {t("home_about_p1")}
              </p>
              <p className="text-base leading-relaxed" style={{ color: '#4a3728' }}>
                {t("home_about_p2")}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_dc7c7b61-87ab-4091-9fab-1b5c30134652/artifacts/3z1yiqdy_IMG_4614.JPG"
                alt="Lam Curry med Ris og Naan"
                className="rounded-lg shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Reserve Your Table - Clean modern style */}
      <section 
        className="py-24 relative"
        style={{ 
          background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)',
        }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-serif-display font-semibold italic mb-6" style={{ color: '#d4af37' }}>
              {t("home_booking_title")}
            </h2>
            <p className="text-lg mb-10 max-w-2xl mx-auto" style={{ color: '#f5e6d3' }}>
              Opplev autentisk indisk mat i en varm og innbydende atmosfære
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/booking">
                <Button className="btn-gold px-12 py-6 text-lg rounded-full">
                  Reserver Bord
                </Button>
              </Link>
              <Link to="/menu">
                <Button 
                  className="px-12 py-6 text-lg rounded-full"
                  style={{ background: 'transparent', border: '2px solid #d4af37', color: '#d4af37' }}
                >
                  Se Meny
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Visit Us / Contact - Modern clean style */}
      <section className="py-24" style={{ background: '#f4e7d8' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-2xl h-96"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1200!2d16.5415!3d68.7985!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x45db6ca050407089%3A0xd2e86c6c5f3c9ea!2sMasala%20Indisk%20restaurant!5e0!3m2!1sno!2sno!4v1710000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Masala Indisk Restaurant - Bysenteret Harstad"
              ></iframe>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-4xl font-serif-display font-semibold italic mb-2" style={{ color: '#2d1810' }}>
                  {t("home_contact_title")}
                </h2>
                <div className="w-20 h-1 rounded" style={{ background: '#d4af37' }}></div>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: '#721111' }} />
                  <div>
                    <h3 className="font-semibold text-lg" style={{ color: '#2d1810' }}>{t("home_contact_address")}</h3>
                    <p style={{ color: '#4a3728' }}>Rikard Kaarbøs plass 1, 9405 Harstad</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Clock className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: '#721111' }} />
                  <div>
                    <h3 className="font-semibold text-lg" style={{ color: '#2d1810' }}>{t("home_contact_hours")}</h3>
                    <p style={{ color: '#4a3728' }}>{t("hours_mon_thu")}</p>
                    <p style={{ color: '#4a3728' }}>{t("hours_fri_sat")}</p>
                    <p style={{ color: '#4a3728' }}>{t("hours_sun")}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Phone className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: '#721111' }} />
                  <div>
                    <h3 className="font-semibold text-lg" style={{ color: '#2d1810' }}>{t("home_contact_phone")}</h3>
                    <p style={{ color: '#4a3728' }}>+47 48 91 51 90</p>
                  </div>
                </div>
              </div>

              <Link to="/contact">
                <Button className="btn-maroon px-10 py-6 text-lg rounded-full mt-4">
                  {t("home_contact_btn")}
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
